import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR
} from '@angular/forms';

@Component({
  selector: 'app-custom-date-picker',
  templateUrl: './custom-date-picker.component.html',
  styleUrls: ['./custom-date-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDatePickerComponent),
      multi: true
    }
  ]
})
export class CustomDatePickerComponent implements OnInit, ControlValueAccessor {
  @Input() placeholder: string = 'Pick Date';
  @Input() disabled: boolean = false;
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() pickerMode: 'date' | 'monthYear' | 'year' = 'date';
  @Input() enableTyping: boolean = true; // allow user to type date
  @Input() inputFormat: 'DD/MM/YYYY' | 'YYYY-MM-DD' | 'D MMMM YYYY' = 'DD/MM/YYYY';

  @Output() dateChange = new EventEmitter<Date>();

  isOpen = false;
  selectedDate: Date | null = null;
  currentMonth: Date = new Date();
  calendarDays: Date[] = [];
  weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  months = [
    'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
    'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
  ];

  isMonthYearView = false;
  isYearView = false;
  yearRange: number[] = [];
  currentYearRange = 0;

  private onChange = (value: any) => {};
  private onTouched = () => {};

  // Holds the raw string shown in the input when typing is enabled
  inputValue: string = '';

  ngOnInit() {
    this.generateCalendar();
    this.generateYearRange();
  }

  writeValue(value: Date | null | string): void {
    // Handle different input types and convert to Date object
    if (value) {
      if (typeof value === 'string') {
        this.selectedDate = new Date(value);
      } else if (value instanceof Date) {
        this.selectedDate = value;
      } else {
        this.selectedDate = new Date(value);
      }
      
      if (this.selectedDate && !isNaN(this.selectedDate.getTime())) {
        this.currentMonth = new Date(this.selectedDate);
        this.generateCalendar();
        this.inputValue = this.getDisplayValue();
      } else {
        this.selectedDate = null;
        this.inputValue = '';
      }
    } else {
      this.selectedDate = null;
      this.inputValue = '';
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggleCalendar(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    this.closeAllViews();

    if (this.isOpen) {
      if (this.pickerMode === 'year') {
        this.isYearView = true;
      } else if (this.pickerMode === 'monthYear') {
        this.isMonthYearView = true;
      }
    }

    this.onTouched();
  }

  closeCalendar(): void {
    this.isOpen = false;
    this.closeAllViews();
  }

  selectDate(date: Date): void {
    this.selectedDate = date;
    this.onChange(date);
    this.dateChange.emit(date);
    this.inputValue = this.getDisplayValue();
    this.closeCalendar();
  }

  previousMonth(): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
    this.generateCalendar();
  }

  nextMonth(): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
    this.generateCalendar();
  }

  generateCalendar(): void {
    if (this.pickerMode !== 'date') return; // Only generate days for 'date' mode

    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    this.calendarDays = [];
    for (let i = 0; i < 42; i++) {
      this.calendarDays.push(new Date(startDate));
      startDate.setDate(startDate.getDate() + 1);
    }
  }

  isCurrentMonth(date: Date): boolean {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return false;
    }
    return date.getMonth() === this.currentMonth.getMonth();
  }

  isSelected(date: Date): boolean {
    if (!this.selectedDate || !(this.selectedDate instanceof Date) || isNaN(this.selectedDate.getTime())) {
      return false;
    }
    return this.selectedDate.toDateString() === date.toDateString();
  }

  isToday(date: Date): boolean {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return false;
    }
    return date.toDateString() === new Date().toDateString();
  }

  isDisabled(date: Date): boolean {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return true;
    }
    if (this.minDate && date < this.minDate) return true;
    if (this.maxDate && date > this.maxDate) return true;
    return false;
  }

  getDisplayValue(): string {
    if (!this.selectedDate || !(this.selectedDate instanceof Date) || isNaN(this.selectedDate.getTime())) {
      return '';
    }
    
    if (this.pickerMode === 'year') {
      return this.selectedDate.getFullYear().toString();
    } else if (this.pickerMode === 'monthYear') {
      return `${this.months[this.selectedDate.getMonth()]} ${this.selectedDate.getFullYear()}`;
    } else {
      // Format based on inputFormat
      return this.formatDate(this.selectedDate);
    }
  }

  getMonthYearDisplay(): string {
    return `${this.months[this.currentMonth.getMonth()]} ${this.currentMonth.getFullYear()}`;
  }

  toggleMonthYearView(): void {
    this.isMonthYearView = !this.isMonthYearView;
    this.isYearView = false;
  }

  toggleYearView(): void {
    this.isYearView = !this.isYearView;
    this.isMonthYearView = false;
  }

  selectMonth(monthIndex: number): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), monthIndex, 1);
    this.generateCalendar();

    if (this.pickerMode === 'monthYear') {
      this.selectDate(this.currentMonth);
    } else {
      this.isMonthYearView = false;
    }
  }

  selectYear(year: number): void {
    this.currentMonth = new Date(year, this.currentMonth.getMonth(), 1);
    this.generateCalendar();

    if (this.pickerMode === 'year') {
      this.selectDate(this.currentMonth);
    } else {
      this.isYearView = false;
      if (this.pickerMode === 'monthYear') {
        this.isMonthYearView = true;
      }
    }
  }

  previousYearRange(): void {
    this.currentYearRange -= 12;
    this.generateYearRange();
  }

  nextYearRange(): void {
    this.currentYearRange += 12;
    this.generateYearRange();
  }

  generateYearRange(): void {
    const currentYear = this.currentMonth.getFullYear();
    const startYear = Math.floor(currentYear / 12) * 12 + this.currentYearRange;
    this.yearRange = Array.from({ length: 12 }, (_, i) => startYear + i);
  }

  goToYearView(): void {
    this.isYearView = true;
    this.isMonthYearView = false;
    this.generateYearRange();
  }

  goToMonthView(): void {
    this.isMonthYearView = true;
    this.isYearView = false;
  }

  closeAllViews(): void {
    this.isMonthYearView = false;
    this.isYearView = false;
  }

  // Handle manual typing
  onInputChange(value: string): void {
    if (!this.enableTyping) return;
    this.inputValue = value;
  }

  onInputBlur(): void {
    if (!this.enableTyping) return;
    const parsed = this.parseUserInput(this.inputValue.trim());
    if (parsed && !isNaN(parsed.getTime())) {
      if (this.minDate && parsed < this.minDate) { this.rejectInvalid(); return; }
      if (this.maxDate && parsed > this.maxDate) { this.rejectInvalid(); return; }
      this.selectedDate = parsed;
      this.onChange(parsed);
      this.dateChange.emit(parsed);
      this.currentMonth = new Date(parsed);
      this.generateCalendar();
      this.inputValue = this.getDisplayValue();
    } else {
      this.rejectInvalid();
    }
  }

  onInputEnter(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onInputBlur();
    }
  }

  private rejectInvalid(): void {
    // Revert to last valid value
    this.inputValue = this.getDisplayValue();
  }

  private formatDate(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const day = pad(date.getDate());
    const monthNum = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    if (this.inputFormat === 'YYYY-MM-DD') {
      return `${year}-${monthNum}-${day}`;
    }
    if (this.inputFormat === 'D MMMM YYYY') {
      const monthName = date.toLocaleDateString('en-US', { month: 'long' });
      return `${date.getDate()} ${monthName} ${year}`;
    }
    // Default DD/MM/YYYY
    return `${day}/${monthNum}/${year}`;
  }

  private parseUserInput(value: string): Date | null {
    if (!value) return null;
    // Try YYYY-MM-DD
    const isoMatch = value.match(/^\d{4}-\d{2}-\d{2}$/);
    if (isoMatch) {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    // Try DD/MM/YYYY
    const dm = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (dm) {
      const day = parseInt(dm[1], 10);
      const month = parseInt(dm[2], 10) - 1;
      const year = parseInt(dm[3], 10);
      const d = new Date(year, month, day);
      return d && d.getMonth() === month && d.getDate() === day && d.getFullYear() === year ? d : null;
    }
    // Try D MMMM YYYY (e.g., 15 January 2024)
    const longMatch = value.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
    if (longMatch) {
      const day = parseInt(longMatch[1], 10);
      const monthName = longMatch[2];
      const year = parseInt(longMatch[3], 10);
      const monthIndex = new Date(`${monthName} 1, 2000`).getMonth();
      const d = new Date(year, monthIndex, day);
      return isNaN(d.getTime()) ? null : d;
    }
    // Fallback Date parsing
    const fallback = new Date(value);
    return isNaN(fallback.getTime()) ? null : fallback;
  }
}